import { useContext, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Tabs, Tab } from '@appkit4/react-components/tabs';
import { Button } from '@appkit4/react-components/button';
import { TextArea } from '@appkit4/react-components/field';
import { Upload } from '@appkit4/react-components/upload';
import { Modal } from '@appkit4/react-components/modal';
import { Tooltip } from '@appkit4/react-components/tooltip';
import Message from '@components/Message/Message';
import HistoryMessage from '@components/HistoryMessage/HistoryMessage';
import ThinkingIndicator from '@components/ThinkingIndicator/ThinkingIndicator';
import { getCreationTimestamp } from '@utils/generateTimestamp';
import { handleGenerate } from '@utils/generateHandler';
import { ModificationOptions, SessionContext } from '@contexts/SessionProvider';
import { UserContext } from '@contexts/UserProvider';
import { useFileProcessor } from '@hooks/index';
import styles from './Chat.module.scss';
import { getEmailInitials, getInitials, getFullNameFromEmail } from '@utils/users';
import { findLastIndex } from 'lodash';
import {
    createUserSession,
    generateSessionTopic,
    terminateGeneration,
    abortActiveStream,
} from '@config/getSessionApis';
import { CurrentSource } from '@utils/index';
import { COPILOT_DETAIL_MARKER, sanitizeErrorForDisplay } from '@utils/errorClassifier';
import { SANDBOX_BUNDLER_URL } from '@config/sandbox';
const Chat = () => {
    const {
        attachFile,
        attachDataFile,
        firstPrompt,
        isNewSession,
        isGenerateComplete,
        thread,
        designSystem,
        templateVersion,
        sessionId,
        sessionOwnerEmail,
        projectCount,
        streamMessageProvider,
        modificationOptions,
        sandpackErrors,
        currentGenerationId,
        isSessionOwner,
        autoFixPrompt,
        isProcessingAttachment,
        attachmentProcessingError,
        processedImage,
        processedDataToon,
        processedDataFileName,
        setCurrentGenerationId,
        setStreamMessageProvider,
        setAttachFile,
        setAttachDataFile,
        setCurrentSource,
        setFirstPrompt,
        setHistory,
        setSessionId,
        setIsNewSession,
        setIsGenerateComplete,
        setThread,
        setSessionName,
        setCommitId,
        setCurrentChatSessionId,
        setStreamMessagesConsumer,
        setShowCodeStream,
        setCurrentHistoryCommit,
        currentHistoryCommit,
        setIsTargetActive,
        setSandpackErrors,
        setSessionOwnerEmail,
        setGenerationError,
        setAutoFixPrompt,
        setAttachmentProcessingError,
        toolStatus,
        setToolStatus,
        setGenerationStatus,
        setGenerationSessionId,
        setPostProcessingFailed,
        setModificationOptions,
    } = useContext(SessionContext);

    const { user, userEmail, userGivenName, userLastName } = useContext(UserContext);

    useFileProcessor();

    const [activeTab, setActiveTab] = useState(0);
    const [prompt, setPrompt] = useState('');
    const [autoFixError, setAutoFixError] = useState('');

    const stripCopilotDetail = (text: string) => {
        if (!text || !text.includes(COPILOT_DETAIL_MARKER)) return text;
        return text.substring(0, text.indexOf(COPILOT_DETAIL_MARKER)).trimEnd();
    };
    // Use computed value for display name
    const displayUserName = !isSessionOwner && sessionOwnerEmail ? getFullNameFromEmail(sessionOwnerEmail) : user.name;

    const [showAttachModal, setShowAttachModal] = useState<boolean>(false);
    const [showAttachFileModal, setShowAttachFileModal] = useState<boolean>(false);
    const [initials, setInitials] = useState('');
    const [needReGenerate, setNeedReGenerate] = useState(false);
    const [lastAutoFixTime, setLastAutoFixTime] = useState<number | null>(null);
    const [showAttachMenu, setShowAttachMenu] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

    const chatMessagesRef = useRef<HTMLDivElement>(null);
    const generationIdRef = useRef<string>('');
    const isGenerateCompleteRef = useRef<boolean>(true);
    const attachMenuRef = useRef<HTMLDivElement>(null);
    const attachButtonRef = useRef<HTMLDivElement>(null);

    let isFirstMessage = true;
    let isFirstHistory = true;

    useEffect(() => {
        if (!isSessionOwner) {
            setInitials(getEmailInitials(sessionOwnerEmail));
        } else {
            if (userGivenName && userLastName) {
                const payload = {
                    givenName: userGivenName,
                    lastName: userLastName,
                };

                setInitials(getInitials(payload));
            }
        }
    }, [userGivenName, userLastName]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: isSessionOwner and sessionOwnerEmail are intentionally omitted as they're stable context values

    useEffect(() => {
        if (firstPrompt && user) {
            setThread([
                [
                    {
                        id: null,
                        chatSessionId: null,
                        message: firstPrompt,
                        messageType: 'question',
                        blobUrl: null,
                        createdAt: getCreationTimestamp(),
                        commitId: null,
                        firstChat: true,
                        file: attachFile,
                        pid: null,
                    },
                ],
            ]);
            setAttachFile(null);
            setAttachDataFile(null);
            setFirstPrompt('');
        }
    }, [firstPrompt, user, attachFile]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: setAttachFile, setFirstPrompt, setThread are stable setter functions from context

    useEffect(() => {
        if (isNewSession) {
            setThread([]);
            setPrompt('');
            setHistory([]);
        }
    }, [isNewSession]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: setHistory and setThread are stable setter functions from context

    const isUserNearBottom = useRef(true);
    const isProgrammaticScroll = useRef(false);
    const userScrolledUp = useRef(false);

    useEffect(() => {
        const el = chatMessagesRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (isProgrammaticScroll.current) return;

            const threshold = 100;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
            isUserNearBottom.current = nearBottom;

            if (!nearBottom) {
                userScrolledUp.current = true;
            } else {
                userScrolledUp.current = false;
            }
        };

        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (userScrolledUp.current) return;

        const el = chatMessagesRef.current;
        if (el && isUserNearBottom.current) {
            isProgrammaticScroll.current = true;
            el.scrollTop = el.scrollHeight;
            requestAnimationFrame(() => {
                isProgrammaticScroll.current = false;
            });
        }
    }, [thread, streamMessageProvider]);

    // keep latest values in refs so unmount cleanup reads current values
    useEffect(() => {
        generationIdRef.current = currentGenerationId;
    }, [currentGenerationId]);

    useEffect(() => {
        isGenerateCompleteRef.current = isGenerateComplete;
        if (!isGenerateComplete) {
            userScrolledUp.current = false;
            isUserNearBottom.current = true;
        }
    }, [isGenerateComplete]);

    // terminate any in-flight generation on unmount
    useEffect(() => {
        return () => {
            abortActiveStream();
            const id = generationIdRef.current;
            const done = isGenerateCompleteRef.current;
            if (id && !done) {
                terminateGeneration(id);
            }
        };
    }, []);

    useEffect(() => {
        if (isGenerateComplete && sandpackErrors.length > 0) {
            const errorItemIndex = findLastIndex(sandpackErrors, (item) => {
                return item.type === 'error';
            });

            const doneItemIndex = findLastIndex(sandpackErrors, (item) => {
                return item.type === 'done';
            });

            const doneItem = sandpackErrors[doneItemIndex];
            if (doneItem) {
                if (!doneItem.compilatonError) {
                    setAutoFixError('');
                    setNeedReGenerate(false);
                    return;
                } else {
                    if (errorItemIndex > 0) {
                        const errorItem = sandpackErrors[errorItemIndex];
                        setTimeout(() => {
                            setAutoFixError(
                                `An error has been detected in the project and requires fixing:\n\n${sanitizeErrorForDisplay(errorItem.message)}`
                            );
                            setNeedReGenerate(true);
                        }, 1000);
                    }
                }
            }
        }
    }, [sandpackErrors, isGenerateComplete]);

    useEffect(() => {
        if (needReGenerate) {
            const now = Date.now();
            if (!lastAutoFixTime || now - lastAutoFixTime > 20000) {
                setSandpackErrors([]);
                autoFix();
                setLastAutoFixTime(now);
            }
            setNeedReGenerate(false);
        }
    }, [needReGenerate, lastAutoFixTime]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: autoFix function and setSandpackErrors are intentionally omitted to prevent unwanted re-triggers

    // Consume autoFixPrompt from EnhancedSandpackRenderer error handling
    useEffect(() => {
        if (autoFixPrompt && autoFixPrompt.prompt && isGenerateComplete && thread.length > 0) {
            console.debug(
                `[AutoFix] Consuming autoFixPrompt (attempt ${autoFixPrompt.attemptNumber}, retryOriginal: ${autoFixPrompt.isRetryingOriginal}):`,
                autoFixPrompt.prompt
            );

            // Clear the autoFixPrompt immediately to prevent re-triggering
            const promptToUse = autoFixPrompt.prompt;
            setAutoFixPrompt(null);

            // Use the prompt to trigger the auto-fix generation
            setAutoFixError(promptToUse);
            setNeedReGenerate(true);
        }
    }, [autoFixPrompt, isGenerateComplete, thread.length, setAutoFixPrompt]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                attachMenuRef.current &&
                !attachMenuRef.current.contains(event.target as Node) &&
                attachButtonRef.current &&
                !attachButtonRef.current.contains(event.target as Node)
            ) {
                setShowAttachMenu(false);
            }
        };

        const handleScroll = () => {
            setShowAttachMenu(false);
        };

        const handleResize = () => {
            setShowAttachMenu(false);
        };

        if (showAttachMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [showAttachMenu]);

    const handleTabChange = (index: number) => {
        setActiveTab(index);
    };

    const handleChange = (value: string) => {
        setPrompt(value);
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleGenerateClick();
        }
    };

    const gtenerateCode = async (
        prompt: string,
        userEmail: string,
        attachFile: File | null,
        modificationOptions: ModificationOptions,
        projectCount: number,
        designSystem: string,
        templateVersion: string,
        sessionId: string,
        setCurrentSource: React.Dispatch<React.SetStateAction<CurrentSource>>,
        setCurrentChatSessionId: React.Dispatch<React.SetStateAction<number>>,
        setStreamMessageProvider: React.Dispatch<React.SetStateAction<any>>,
        chatSessionId?: number,
        commitId?: string
    ) => {
        await handleGenerate(
            prompt,
            userEmail,
            attachFile,
            attachDataFile,
            modificationOptions,
            projectCount,
            designSystem,
            templateVersion,
            sessionId,
            currentGenerationId,
            setCurrentGenerationId,
            setCurrentSource,
            setCurrentChatSessionId,
            setStreamMessageProvider,
            setIsGenerateComplete,
            setShowCodeStream,
            setStreamMessagesConsumer,
            setHistory,
            setThread,
            setCurrentHistoryCommit,
            setGenerationError,
            setToolStatus,
            setGenerationStatus,
            setPostProcessingFailed,
            chatSessionId,
            commitId,
            processedImage,
            processedDataToon,
            processedDataFileName
        );
    };

    const removeTargetSelect = () => {
        const iframe: any = document.querySelector('iframe');
        if (iframe) {
            iframe.contentWindow.postMessage({ action: 'closeSelect', type: 'rc' }, SANDBOX_BUNDLER_URL);
        }
        setIsTargetActive(false);
    };

    const autoFix = async () => {
        removeTargetSelect();
        setIsNewSession(false);
        setAttachFile(null);
        setAttachDataFile(null);
        setIsGenerateComplete(false);
        setShowCodeStream(true);
        setGenerationStatus('Fixing errors found...');
        setStreamMessageProvider([]);
        setStreamMessagesConsumer([]);
        // Strip copilot detail section so the chat bubble only shows the clean summary
        const displayMessage = autoFixError.includes(COPILOT_DETAIL_MARKER)
            ? autoFixError.substring(0, autoFixError.indexOf(COPILOT_DETAIL_MARKER)).trimEnd()
            : autoFixError;
        setThread((prev) => [
            ...prev,
            [
                {
                    id: null,
                    chatSessionId: null,
                    message: displayMessage,
                    messageType: 'question',
                    blobUrl: null,
                    createdAt: getCreationTimestamp(),
                    commitId: null,
                    firstChat: false,
                    file: attachFile,
                    pid: null,
                },
            ],
        ]);
        let lastMessageCommitId = '';
        let lastMessageChatSessionId = '';
        await new Promise((resolve) => setTimeout(resolve, 0));

        // Use currentHistoryCommit if available (user selected a specific history point)
        // Otherwise, fall back to finding the latest commitId from thread
        if (currentHistoryCommit) {
            lastMessageCommitId = currentHistoryCommit;
            // Find the chatSessionId for the selected commit
            for (let i = thread.length - 1; i >= 0; i--) {
                for (let j = thread[i].length - 1; j >= 0; j--) {
                    if (thread[i][j].commitId === currentHistoryCommit) {
                        lastMessageChatSessionId = thread[i][j].chatSessionId;
                        break;
                    }
                }
                if (lastMessageChatSessionId) {
                    break;
                }
            }
        } else {
            // Fallback: find the latest commitId
            for (let i = thread.length - 1; i >= 0; i--) {
                for (let j = thread[i].length - 1; j > 0; j--) {
                    if (thread[i][j].commitId) {
                        lastMessageCommitId = thread[i][j].commitId;
                        lastMessageChatSessionId = thread[i][j].chatSessionId;
                        break; // Break inner loop
                    }
                }
                if (lastMessageCommitId) {
                    break; // Break outer loop
                }
            }
        }

        if (lastMessageCommitId) {
            setCommitId(lastMessageCommitId);
        }

        if (!lastMessageCommitId || !lastMessageChatSessionId) {
            console.error('[DDMPC001] Cannot send follow-up: No commitId found for existing session');
            setGenerationError({
                message: 'We are experiencing a temporary issue. Please try again in 2 minutes.',
                error: { code: 'DDMPC001', message: 'Project context lost - commitId not found in autoFix' },
                prompt: autoFixError,
                timestamp: Date.now(),
            });
            setIsGenerateComplete(true);
            setAutoFixError('');
            return;
        }

        // Ensure commitId is updated before calling handleGenerate
        await new Promise((resolve) => setTimeout(resolve, 0));

        await gtenerateCode(
            autoFixError,
            userEmail,
            attachFile,
            modificationOptions,
            projectCount,
            designSystem,
            templateVersion,
            sessionId,
            setCurrentSource,
            setCurrentChatSessionId,
            setStreamMessageProvider,
            Number(lastMessageChatSessionId), // Pass the updated commitId
            lastMessageCommitId
        );
        setAutoFixError('');
    };

    // Check whether any message in the thread has a commitId,
    // indicating at least one successful generation has completed.
    const threadHasCommitId = (t: any[][]): boolean => {
        for (const group of t) {
            for (const msg of group) {
                if (msg.commitId) return true;
            }
        }
        return false;
    };

    const handleGenerateClick = async () => {
        removeTargetSelect();
        if (prompt.trim().length > 0 && user && isGenerateComplete) {
            const currentPrompt = prompt;
            const currentAttachFile = attachFile;
            const currentAttachDataFile = attachDataFile;
            const currentModificationOptions = modificationOptions;
            setPrompt('');
            setModificationOptions({
                componentType: '',
                dataComponentFile: '',
                dataComponentStartLine: '',
                dataComponentName: '',
                elementContent: '',
                elementOuterHTML: '',
                elementTagName: '',
            });
            setIsNewSession(false);
            setIsGenerateComplete(false);
            setShowCodeStream(true);
            setGenerationSessionId(sessionId);
            setStreamMessageProvider([]);
            setStreamMessagesConsumer([]);
            setToolStatus(null);
            setGenerationStatus(null);
            setPostProcessingFailed(false);
            // Treat as first prompt if thread is empty OR no generation ever succeeded
            // (no commitId anywhere). This prevents DDMPC001 after a failed first generation.
            const isFirstPrompt = thread.length === 0 || !threadHasCommitId(thread);
            if (!isFirstPrompt) {
                setThread((prev) => [
                    ...prev,
                    [
                        {
                            id: null,
                            chatSessionId: null,
                            message: currentPrompt,
                            messageType: 'question',
                            blobUrl: null,
                            createdAt: getCreationTimestamp(),
                            commitId: null,
                            firstChat: false,
                            file: currentAttachFile,
                            pid: null,
                        },
                    ],
                ]);
                let lastMessageCommitId = '';
                let lastMessageChatSessionId = '';
                await new Promise((resolve) => setTimeout(resolve, 0));

                // Use currentHistoryCommit if available (user selected a specific history point)
                // Otherwise, fall back to finding the latest commitId from thread
                if (currentHistoryCommit) {
                    lastMessageCommitId = currentHistoryCommit;
                    // Find the chatSessionId for the selected commit
                    for (let i = thread.length - 1; i >= 0; i--) {
                        for (let j = thread[i].length - 1; j >= 0; j--) {
                            if (thread[i][j].commitId === currentHistoryCommit) {
                                lastMessageChatSessionId = thread[i][j].chatSessionId;
                                break;
                            }
                        }
                        if (lastMessageChatSessionId) {
                            break;
                        }
                    }
                } else {
                    // Fallback: find the latest commitId
                    for (let i = thread.length - 1; i >= 0; i--) {
                        for (let j = thread[i].length - 1; j > 0; j--) {
                            if (thread[i][j].commitId) {
                                lastMessageCommitId = thread[i][j].commitId;
                                lastMessageChatSessionId = thread[i][j].chatSessionId;
                                break; // Break inner loop
                            }
                        }
                        if (lastMessageCommitId) {
                            break; // Break outer loop
                        }
                    }
                }

                if (lastMessageCommitId) {
                    setCommitId(lastMessageCommitId);
                }

                if (!lastMessageCommitId || !lastMessageChatSessionId) {
                    console.error('[DDMPC001] Cannot send follow-up: No commitId found for existing session');
                    setGenerationError({
                        message: 'We are experiencing a temporary issue. Please try again in 2 minutes.',
                        error: {
                            code: 'DDMPC001',
                            message: 'Project context lost - commitId not found in handleGenerateClick',
                        },
                        prompt: currentPrompt,
                        timestamp: Date.now(),
                    });
                    setIsGenerateComplete(true);
                    return;
                }

                // Ensure commitId is updated before calling handleGenerate
                await new Promise((resolve) => setTimeout(resolve, 0));

                gtenerateCode(
                    currentPrompt,
                    userEmail,
                    currentAttachFile,
                    currentModificationOptions,
                    projectCount,
                    designSystem,
                    templateVersion,
                    sessionId,
                    setCurrentSource,
                    setCurrentChatSessionId,
                    setStreamMessageProvider,
                    Number(lastMessageChatSessionId), // Pass the chatSessionId based on selected commit
                    lastMessageCommitId // Pass the selected commitId (from history or latest)
                );
                // Clear attachFile after follow-up generation has started
                setAttachFile(null);
                setAttachDataFile(null);
            } else {
                setFirstPrompt(currentPrompt);
                let newSessionId = '';
                const sessionResult = await createUserSession(userEmail, designSystem, templateVersion);
                const sessionId = sessionResult.resultObject.sessionId;
                if (sessionId) {
                    setSessionId(sessionId);
                    newSessionId = sessionId;
                    setSessionOwnerEmail(userEmail);
                }
                const newSessionTopic = await generateSessionTopic(
                    userEmail,
                    newSessionId,
                    currentPrompt,
                    projectCount > 1,
                    currentAttachFile,
                    currentAttachDataFile
                );
                const topic = newSessionTopic.resultObject.payload.topic;
                setSessionName(topic);
                gtenerateCode(
                    currentPrompt,
                    userEmail,
                    currentAttachFile,
                    currentModificationOptions,
                    projectCount,
                    designSystem,
                    templateVersion,
                    newSessionId,
                    setCurrentSource,
                    setCurrentChatSessionId,
                    setStreamMessageProvider
                );
                // Clear attachFile after first generation has started
                setAttachFile(null);
                setAttachDataFile(null);
            }
        }
    };

    const handleStopBtnClick = async () => {
        abortActiveStream();
        await terminateGeneration(currentGenerationId);
        setThread((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
        setIsGenerateComplete(true);
        setShowCodeStream(false);
        setGenerationStatus(null);
        setStreamMessagesConsumer([]);
        setStreamMessageProvider([]);
    };

    const handleUploadChange = (file: any) => {
        const originFile = file instanceof File ? file : file?.originFile;
        setAttachFile(originFile);
        setShowAttachModal(false);
    };

    const handleDataFileUploadChange = (file: any) => {
        const originFile = file instanceof File ? file : file?.originFile;
        setAttachDataFile(originFile);
        setShowAttachFileModal(false);
    };

    const handleAttachButtonClick = () => {
        if (attachButtonRef.current) {
            const rect = attachButtonRef.current.getBoundingClientRect();
            // Position menu above the button
            const menuHeight = 100; // Approximate height of menu with 2 items
            setMenuPosition({
                top: rect.top + window.scrollY - menuHeight - 4,
                left: rect.left + window.scrollX,
            });
        }
        setShowAttachMenu(!showAttachMenu);
    };

    const renderPromptArea = () => {
        const icons: any = {
            Text: 'icon-text-outline',
            image: 'icon-image-outline',
            Component: 'icon-vector-outline',
        };

        const icon = icons[modificationOptions?.componentType]
            ? icons[modificationOptions.componentType]
            : 'icon-vector-outline';

        if (modificationOptions.componentType) {
            const { componentType } = modificationOptions;
            const textAreaWithType = (
                <TextArea
                    autosize={{
                        maxRows: prompt ? 4 : 2,
                    }}
                    value={prompt}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    title="Do you want to change anything?"
                    disabled={!isSessionOwner}
                />
            );

            const wrappedTextArea = !isSessionOwner ? (
                <Tooltip
                    trigger="hover"
                    position="top"
                    distance={4}
                    appendAfterTarget={false}
                    content="Editing is not available in this view">
                    {textAreaWithType}
                </Tooltip>
            ) : (
                textAreaWithType
            );

            return (
                <div className={styles['prompt-area']}>
                    <div className={styles['component-type']}>
                        <span className={`Appkit4-icon ${icon}`}></span>
                        <span>{componentType}</span>
                    </div>
                    {wrappedTextArea}
                </div>
            );
        }

        const textArea = (
            <TextArea
                autosize={{
                    maxRows: prompt ? 4 : 1,
                }}
                value={prompt}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                title="Do you want to change anything?"
                disabled={!isSessionOwner}
            />
        );

        return !isSessionOwner ? (
            <Tooltip
                trigger="hover"
                position="top"
                distance={4}
                appendAfterTarget={false}
                content="Editing is not available in this view">
                {textArea}
            </Tooltip>
        ) : (
            textArea
        );
    };

    return (
        <div className={styles.chat}>
            <Tabs type="filled" activeIndex={activeTab} onTabChange={handleTabChange} stretched>
                <Tab label="Chat" value="Chat" icon="icon-comment-outline"></Tab>
                <Tab label="History" value="History" icon="icon-revert-outline" disabled={!isSessionOwner}></Tab>
            </Tabs>
            <div className={styles['chat-wrapper']}>
                {activeTab === 0 && (
                    <div className={styles['chat-content']}>
                        <div className={styles['chat-messages']} ref={chatMessagesRef}>
                            {thread.map((messagePair, i) => {
                                return messagePair.map((message: any, j: number) => {
                                    // Render the first pair of messages where firstChat = true
                                    if (isFirstMessage && message.firstChat) {
                                        if (message.messageType === 'answer') {
                                            isFirstMessage = false;
                                        }
                                        return (
                                            <Message
                                                key={`first-${i}-${j}`}
                                                author={
                                                    message.messageType === 'question'
                                                        ? displayUserName
                                                        : 'Design Draft'
                                                }
                                                timestamp={message.createdAt}
                                                file={message.file}
                                                blobUrl={message.blobUrl}
                                                avatarName={initials}
                                                type={message.messageType}
                                                mode="normal">
                                                {stripCopilotDetail(message.message)}
                                            </Message>
                                        );
                                    }

                                    // Render all pairs of messages where firstChat = false
                                    if (!message.firstChat) {
                                        return (
                                            <Message
                                                key={`non-first-${i}-${j}`}
                                                author={
                                                    message.messageType === 'question'
                                                        ? displayUserName
                                                        : 'Design Draft'
                                                }
                                                timestamp={message.createdAt}
                                                file={message.file}
                                                blobUrl={message.blobUrl}
                                                avatarName={initials}
                                                type={message.messageType}
                                                mode="normal">
                                                {stripCopilotDetail(message.message)}
                                            </Message>
                                        );
                                    }

                                    return null; // Skip other cases
                                });
                            })}
                            {!isGenerateComplete && streamMessageProvider.length === 0 && (
                                <ThinkingIndicator statusMessage={toolStatus} />
                            )}
                            {streamMessageProvider.length > 0 && !isGenerateComplete ? (
                                <Message
                                    key={'streaming-message'}
                                    author={'Design Draft'}
                                    timestamp={new Date().getTime()}
                                    avatarName={initials}
                                    type={'answer'}
                                    mode={'stream'}
                                    streamMessagesPool={[...streamMessageProvider]} //add a empty code to force prism.js apply highlight
                                ></Message>
                            ) : (
                                ''
                            )}
                        </div>
                        <div className={styles['chat-input-wrapper']}>
                            {renderPromptArea()}
                            <div className={styles['attached-files']}>
                                {attachmentProcessingError && (
                                    <div className={styles['attachment-error']}>
                                        <span className="Appkit4-icon icon-alert-outline"></span>
                                        <span>{attachmentProcessingError}</span>
                                        <span
                                            onClick={() => setAttachmentProcessingError(null)}
                                            className="Appkit4-icon icon-close-outline"
                                            role="button"
                                            aria-label="Dismiss error"></span>
                                    </div>
                                )}
                                <div
                                    className={`${styles['attached-file']} ${attachFile && isProcessingAttachment ? styles['processing'] : ''}`}
                                    style={{
                                        display: attachFile ? 'flex' : 'none',
                                    }}>
                                    <div>
                                        {attachFile && isProcessingAttachment ? (
                                            <span className={styles['spinner']}></span>
                                        ) : (
                                            <span className="Appkit4-icon icon-image-outline"></span>
                                        )}
                                        <span className={styles.file}>{attachFile && attachFile.name}</span>
                                    </div>
                                    <span
                                        onClick={() => setAttachFile(null)}
                                        className="Appkit4-icon icon-close-outline"></span>
                                </div>
                                <div
                                    className={`${styles['attached-file']} ${attachDataFile && isProcessingAttachment ? styles['processing'] : ''}`}
                                    style={{
                                        display: attachDataFile ? 'flex' : 'none',
                                    }}>
                                    <div>
                                        {attachDataFile && isProcessingAttachment ? (
                                            <span className={styles['spinner']}></span>
                                        ) : (
                                            <span className="Appkit4-icon icon-document-file-outline"></span>
                                        )}
                                        <span className={styles.file}>{attachDataFile && attachDataFile.name}</span>
                                    </div>
                                    <span
                                        onClick={() => setAttachDataFile(null)}
                                        className="Appkit4-icon icon-close-outline"></span>
                                </div>
                            </div>
                            <div className={styles['button-wrapper']}>
                                <div ref={attachButtonRef}>
                                    <Button
                                        kind="tertiary"
                                        icon="icon-paperclip-outline"
                                        aria-label="Attach files button"
                                        role="button"
                                        onClick={handleAttachButtonClick}
                                        disabled={!isSessionOwner}>
                                        Attach
                                    </Button>
                                </div>
                                {showAttachMenu &&
                                    menuPosition &&
                                    createPortal(
                                        <div
                                            ref={attachMenuRef}
                                            className={styles['attach-menu']}
                                            style={{
                                                position: 'absolute',
                                                top: `${menuPosition.top}px`,
                                                left: `${menuPosition.left}px`,
                                            }}>
                                            <div
                                                className={`${styles['attach-menu-item']} ${attachFile ? styles['disabled'] : ''}`}
                                                onClick={() => {
                                                    if (!attachFile) {
                                                        setShowAttachModal(true);
                                                        setShowAttachMenu(false);
                                                    }
                                                }}>
                                                <span className="Appkit4-icon icon-image-outline"></span>
                                                <span>Attach image</span>
                                            </div>
                                            <div
                                                className={`${styles['attach-menu-item']} ${attachDataFile ? styles['disabled'] : ''}`}
                                                onClick={() => {
                                                    if (!attachDataFile) {
                                                        setShowAttachFileModal(true);
                                                        setShowAttachMenu(false);
                                                    }
                                                }}>
                                                <span className="Appkit4-icon icon-document-file-outline"></span>
                                                <span>Attach data</span>
                                            </div>
                                        </div>,
                                        document.body
                                    )}

                                {isGenerateComplete ? (
                                    <Button
                                        kind="primary"
                                        icon="icon-paper-airplane-outline"
                                        className="pink-button"
                                        onClick={handleGenerateClick}
                                        disabled={
                                            prompt.trim().length === 0 ||
                                            !isGenerateComplete ||
                                            !isSessionOwner ||
                                            isProcessingAttachment
                                        }
                                        aria-label="Generate button"
                                        role="button">
                                        Generate
                                    </Button>
                                ) : (
                                    <Button
                                        kind="primary"
                                        className="pink-button"
                                        onClick={handleStopBtnClick}
                                        disabled={!isSessionOwner}
                                        aria-label="Generate button"
                                        role="button">
                                        Stop
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === 1 && (
                    <div className={styles['history-content']}>
                        {thread && thread.length > 0 ? (
                            thread.map((messagePair, i) => {
                                return messagePair.map((message: any) => {
                                    if (message.messageType === 'question') return null;

                                    // Render the first pair of messages where firstChat = true
                                    if (isFirstHistory && message.firstChat) {
                                        isFirstHistory = false;
                                        const historyMsg = stripCopilotDetail(messagePair[0].message);
                                        let errorStatus = false;
                                        if (
                                            thread[i + 1] &&
                                            thread[i + 1][0].message.indexOf('The project have error,') !== -1
                                        ) {
                                            errorStatus = true;
                                        }
                                        return (
                                            <HistoryMessage
                                                key={i}
                                                author={displayUserName}
                                                version={(i + 1).toString()}
                                                message={historyMsg}
                                                createdAt={message.createdAt}
                                                chatSessionId={message.chatSessionId}
                                                commitId={message.commitId}
                                                isCurrent={message.commitId === currentHistoryCommit}
                                                errorStatus={errorStatus}
                                            />
                                        );
                                    }

                                    // Render all pairs of messages where firstChat = false
                                    if (!message.firstChat) {
                                        let errorStatus = false;
                                        if (
                                            thread[i + 1] &&
                                            thread[i + 1][0].message.indexOf('The project have error,') !== -1
                                        ) {
                                            errorStatus = true;
                                        }
                                        return (
                                            <HistoryMessage
                                                key={i}
                                                author={displayUserName}
                                                version={(i + 1).toString()}
                                                message={stripCopilotDetail(messagePair[0].message)}
                                                createdAt={message.createdAt}
                                                chatSessionId={message.chatSessionId}
                                                commitId={message.commitId}
                                                isCurrent={message.commitId === currentHistoryCommit}
                                                errorStatus={errorStatus}
                                            />
                                        );
                                    }

                                    return null; // Skip other cases
                                });
                            })
                        ) : (
                            <div className={styles['no-history']}>
                                <span>No history available</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Modal visible={showAttachModal} onCancel={() => setShowAttachModal(false)} title="Attach your image file">
                <Upload
                    onChange={handleUploadChange}
                    multiple={false}
                    autoUpload={false}
                    acceptFileType=".JPEG,.JPG,.PNG,.GIF,.WEBP"
                    uploadInstruction="You can only attach 1 image file for each round. The max file size is 25MB."
                    maxFileSize={25 * 1024 * 1024}
                    config={{
                        trigger: false,
                        type: 'inline',
                        size: true,
                    }}
                />
            </Modal>
            <Modal
                visible={showAttachFileModal}
                onCancel={() => setShowAttachFileModal(false)}
                title="Attach your data file">
                <Upload
                    onChange={handleDataFileUploadChange}
                    multiple={false}
                    autoUpload={false}
                    acceptFileType=".csv,.CSV,.xlsx,.XLSX,.xls,.XLS"
                    uploadInstruction="You can attach Excel or CSV files. The max file size is 10MB."
                    maxFileSize={10 * 1024 * 1024}
                    config={{
                        trigger: false,
                        type: 'inline',
                        size: true,
                    }}
                />
            </Modal>
        </div>
    );
};

export default Chat;
